/**
 * useNetworkMonitor — LG WebOS network detection hook for SolidJS.
 *
 * Uses three methods for maximum reliability across WebOS 3.x–24:
 *   1. Luna Service API (com.palm.connectionmanager) — most reliable
 *   2. window online/offline events — fallback
 *   3. navigator.onLine polling — last-resort for WebOS 3.x
 *
 * Exposes reactive signals: isConnected, isInternetReachable, isReconnecting,
 * connectionType, reconnectAttempt.
 */

import { createSignal, onCleanup } from "solid-js";

export type ConnectionType = "wifi" | "ethernet" | "unknown";

export interface NetworkMonitorReturn {
  isConnected: () => boolean;
  isInternetReachable: () => boolean;
  isReconnecting: () => boolean;
  connectionType: () => ConnectionType;
  reconnectAttempt: () => number;
  /** Manually trigger a reachability check + resume cycle */
  retryNow: () => void;
}

// ── WebOS version detection ─────────────────────────────────────────
function getWebOSVersion(): number {
  const ua = navigator.userAgent;
  // WebOS 6.x / 22 / 23 / 24 identify as "Web0S" or "WebOS" with version
  // WebOS 3.x: Chrome/38, WebOS 4.x: Chrome/53, WebOS 5.x: Chrome/68
  // WebOS 6.x: Chrome/79+, WebOS 22+: Chrome/87+
  const chromeMatch = ua.match(/Chrome\/(\d+)/);
  if (!chromeMatch) return 3; // fallback to oldest
  const chromeVer = parseInt(chromeMatch[1], 10);
  if (chromeVer >= 87) return 22; // WebOS 22+
  if (chromeVer >= 79) return 6;  // WebOS 6.x
  if (chromeVer >= 68) return 5;  // WebOS 5.x
  if (chromeVer >= 53) return 4;  // WebOS 4.x
  return 3; // WebOS 3.x
}

const REACHABILITY_URL = "https://connectivitycheck.gstatic.com/generate_204";
const MAX_REACHABILITY_RETRIES = 5;
const REACHABILITY_RETRY_INTERVAL = 2000;
const REACHABILITY_TIMEOUT = 3000;
const OFFLINE_DEBOUNCE_MS = 700;

export function useNetworkMonitor(): NetworkMonitorReturn {
  const [isConnected, setIsConnected] = createSignal(navigator.onLine !== false);
  const [isInternetReachable, setIsInternetReachable] = createSignal(false);
  const [isReconnecting, setIsReconnecting] = createSignal(false);
  const [connectionType, setConnectionType] = createSignal<ConnectionType>("unknown");
  const [reconnectAttempt, setReconnectAttempt] = createSignal(0);

  const webOSVersion = getWebOSVersion();
  const stabilizationDelay = webOSVersion >= 22 ? 1200 : 2000;

  let offlineDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  let reachabilityRetryTimer: ReturnType<typeof setTimeout> | null = null;
  let pollingTimer: ReturnType<typeof setInterval> | null = null;
  let lunaSubscription: any = null;
  let lunaAvailable = false;
  let destroyed = false;

  // ── Reachability check ────────────────────────────────────────────
  function checkReachability(): Promise<boolean> {
    return Promise.race([
      fetch(REACHABILITY_URL, { method: "HEAD", mode: "no-cors", cache: "no-store" })
        .then(() => true)
        .catch(() => false),
      new Promise<boolean>((resolve) =>
        setTimeout(() => resolve(false), REACHABILITY_TIMEOUT)
      ),
    ]);
  }

  async function verifyAndSignalOnline(retryCount = 0): Promise<void> {
    if (destroyed) return;

    setIsReconnecting(true);
    setReconnectAttempt(retryCount + 1);

    const reachable = await checkReachability();

    if (destroyed) return;

    if (reachable) {
      setIsInternetReachable(true);
      setIsReconnecting(false);
      setReconnectAttempt(0);
      return;
    }

    // Retry up to MAX_REACHABILITY_RETRIES
    if (retryCount < MAX_REACHABILITY_RETRIES - 1) {
      reachabilityRetryTimer = setTimeout(() => {
        verifyAndSignalOnline(retryCount + 1);
      }, REACHABILITY_RETRY_INTERVAL);
    } else {
      // All retries exhausted — stay on overlay, keep waiting
      setIsReconnecting(false);
      // Don't set isInternetReachable to true — overlay stays visible
    }
  }

  // ── Handlers ──────────────────────────────────────────────────────
  function handleOffline(): void {
    if (destroyed) return;
    // Clear any pending online verification
    if (reachabilityRetryTimer) {
      clearTimeout(reachabilityRetryTimer);
      reachabilityRetryTimer = null;
    }

    // Debounce to avoid false positives on WiFi band switching
    if (offlineDebounceTimer) clearTimeout(offlineDebounceTimer);
    offlineDebounceTimer = setTimeout(() => {
      if (destroyed) return;
      setIsConnected(false);
      setIsInternetReachable(false);
      setIsReconnecting(false);
      setReconnectAttempt(0);
    }, OFFLINE_DEBOUNCE_MS);
  }

  function handleOnline(): void {
    if (destroyed) return;
    // Cancel offline debounce if it's still pending
    if (offlineDebounceTimer) {
      clearTimeout(offlineDebounceTimer);
      offlineDebounceTimer = null;
    }

    setIsConnected(true);

    // Wait for WebOS network stack to stabilize before verifying
    setTimeout(() => {
      if (destroyed) return;
      verifyAndSignalOnline(0);
    }, stabilizationDelay);
  }

  // ── Method 1: Luna Service API ────────────────────────────────────
  function setupLuna(): void {
    try {
      const webOS = (window as any).webOS;
      if (!webOS || !webOS.service || !webOS.service.request) {
        return;
      }

      lunaSubscription = webOS.service.request(
        "luna://com.palm.connectionmanager",
        {
          method: "getStatus",
          parameters: { subscribe: true },
          onSuccess: (res: any) => {
            lunaAvailable = true;

            // Determine connection type
            if (res.wired && res.wired.state === "connected") {
              setConnectionType("ethernet");
            } else if (res.wifi && res.wifi.state === "connected") {
              setConnectionType("wifi");
            } else {
              setConnectionType("unknown");
            }

            if (res.isInternetConnectionAvailable === true) {
              handleOnline();
            } else if (res.isInternetConnectionAvailable === false) {
              handleOffline();
            }
          },
          onFailure: () => {
            // Luna not available — rely on Methods 2 and 3
            lunaAvailable = false;
          },
        }
      );
    } catch {
      lunaAvailable = false;
    }
  }

  // ── Method 2: window online/offline events ────────────────────────
  function onWindowOnline() { handleOnline(); }
  function onWindowOffline() { handleOffline(); }

  window.addEventListener("online", onWindowOnline);
  window.addEventListener("offline", onWindowOffline);

  // ── Method 3: navigator.onLine polling for WebOS 3.x ─────────────
  function setupPolling(): void {
    let lastOnLine = navigator.onLine;
    pollingTimer = setInterval(() => {
      const current = navigator.onLine;
      if (current !== lastOnLine) {
        lastOnLine = current;
        if (current) {
          handleOnline();
        } else {
          handleOffline();
        }
      }
    }, 3000);
  }

  // ── Initialize ────────────────────────────────────────────────────
  setupLuna();

  // Always set up polling as a safety net (low cost, high reliability)
  setupPolling();

  // ── Manual retry ──────────────────────────────────────────────────
  function retryNow(): void {
    if (destroyed) return;
    // Clear any pending timers
    if (reachabilityRetryTimer) {
      clearTimeout(reachabilityRetryTimer);
      reachabilityRetryTimer = null;
    }

    if (isConnected()) {
      // Network layer is connected, re-verify reachability
      verifyAndSignalOnline(0);
    } else {
      // Try to detect if we're actually back online
      if (navigator.onLine) {
        handleOnline();
      } else {
        // Trigger a reachability check anyway
        verifyAndSignalOnline(0);
      }
    }
  }

  // ── Cleanup ───────────────────────────────────────────────────────
  onCleanup(() => {
    destroyed = true;

    window.removeEventListener("online", onWindowOnline);
    window.removeEventListener("offline", onWindowOffline);

    if (offlineDebounceTimer) clearTimeout(offlineDebounceTimer);
    if (reachabilityRetryTimer) clearTimeout(reachabilityRetryTimer);
    if (pollingTimer) clearInterval(pollingTimer);

    // Cancel Luna subscription
    if (lunaSubscription) {
      try {
        lunaSubscription.cancel();
      } catch {
        // ignore
      }
      lunaSubscription = null;
    }
  });

  return {
    isConnected,
    isInternetReachable,
    isReconnecting,
    connectionType,
    reconnectAttempt,
    retryNow,
  };
}
